import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import moment from 'moment'

import { createComment, deleteComment, getComments } from '../../api/comments'
import shrinkPhoto from '../../utils/shrinkPhoto'
import { trackAction } from '../../utils/tracking'
import UserPhoto from '../UserPhoto'
import FamilyPhotos from './FamilyPhotos'

import './style.css'

const MAX_LENGTH = 2000
// Photos one post can carry (the API allows the same)
const MAX_PHOTOS = 4

const CameraIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1.4-2h6.2l1.4 2h2A1.5 1.5 0 0 1 20 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 17.5v-9Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="13" r="3.2" stroke="currentColor" strokeWidth="1.8" />
  </svg>
)

/**
 * Every photo in the conversation, newest first, for the recipe's family photos.
 * Each one: { id, image, comment_id, user, created_at }
 */
export const photosFromThreads = (threads) =>
  threads
    .flatMap((thread) => [thread, ...thread.replies])
    .filter((comment) => !comment.deleted)
    .flatMap((comment) =>
      (comment.photos || []).map((photo) => ({
        ...photo,
        comment_id: comment.id,
        user: comment.user,
        created_at: comment.created_at,
      }))
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at) || b.id - a.id)

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('')

const errorMessage = (e) => {
  if (e?.response?.status === 403) {
    return 'You can only post on recipes in groups you belong to.'
  }
  if (e?.response?.status === 413) {
    return 'Those photos are too big to send. Try fewer at a time.'
  }
  // The API's own words ({ field: message }) for a photo it can't use, or too many
  const problems = e?.response?.status === 422 ? e.response.data?.data : null
  const problem = problems && typeof problems === 'object' ? Object.values(problems)[0] : null
  return problem ? String(problem) : 'That did not go through. Please try again.'
}

const CommentForm = ({ placeholder, submitText, autoFocus, onSubmit, onCancel, inputId }) => {
  const [body, setBody] = useState('')
  const [photos, setPhotos] = useState([])
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const photoUrls = useMemo(() => photos.map((photo) => URL.createObjectURL(photo)), [photos])
  useEffect(() => () => photoUrls.forEach((url) => URL.revokeObjectURL(url)), [photoUrls])
  const room = MAX_PHOTOS - photos.length
  const canPost = (!!body.trim() || photos.length > 0) && !sending

  const onPhotosChosen = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, Math.max(room, 0))
    e.target.value = ''
    if (files.length) {
      setError('')
      const shrunk = await Promise.all(files.map(shrinkPhoto))
      setPhotos((current) => [...current, ...shrunk].slice(0, MAX_PHOTOS))
    }
  }

  const submit = (e) => {
    e.preventDefault()
    if (!canPost) {
      return
    }

    setSending(true)
    setError('')
    onSubmit(body.trim(), photos)
      .then(() => {
        setBody('')
        setPhotos([])
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setSending(false))
  }

  return (
    <form className="commentForm" onSubmit={submit}>
      <textarea
        className="commentInput"
        value={body}
        maxLength={MAX_LENGTH}
        placeholder={placeholder}
        autoFocus={autoFocus}
        rows={body.includes('\n') || body.length > 80 ? 4 : 2}
        onChange={(e) => setBody(e.target.value)}
        aria-label={placeholder}
      />
      {photoUrls.length ? (
        <div className="commentFormPhotos">
          {photoUrls.map((url, index) => (
            <div className="commentFormPhoto" key={url}>
              <img src={url} alt={`Photo ${index + 1} to post`} />
              <button
                type="button"
                className="commentFormPhotoRemove"
                aria-label={`Remove photo ${index + 1}`}
                onClick={() => setPhotos(photos.filter((_, i) => i !== index))}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      ) : null}
      {error ? <p className="commentError">{error}</p> : null}
      <div className="commentFormActions">
        {room > 0 ? (
          <>
            <input
              id={inputId}
              className="commentPhotoInput"
              type="file"
              accept="image/*"
              multiple
              onChange={onPhotosChosen}
            />
            <label className="commentAddPhoto" htmlFor={inputId}>
              <CameraIcon />
              {photos.length ? 'Add another' : 'Add photos'}
            </label>
          </>
        ) : null}
        {onCancel ? (
          <button type="button" className="commentLinkButton" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="commentSubmit" disabled={!canPost}>
          {sending ? 'Posting…' : submitText}
        </button>
      </div>
    </form>
  )
}

const CommentPhotos = ({ photos, author, onOpen }) =>
  photos.length ? (
    <div className={`commentPhotos ${photos.length === 1 ? 'single' : ''}`}>
      {photos.map((photo, index) => (
        <button
          type="button"
          key={photo.id}
          className="commentPhoto"
          onClick={() => onOpen(photo)}
          aria-label={`Open photo ${index + 1} from ${author}`}
        >
          <img src={photo.image} alt="" loading="lazy" />
        </button>
      ))}
    </div>
  ) : null

const Comment = ({ comment, showGroup, highlighted, onReply, onDelete, onOpenPhoto }) => {
  const { id, user, body, deleted, created_at, group_name, reply_to_name, can_delete } = comment
  const photos = comment.photos || []

  return (
    <div
      id={`comment-${id}`}
      className={`comment ${comment.thread_id ? 'commentReply' : ''} ${
        highlighted ? 'commentHighlighted' : ''
      }`}
    >
      <UserPhoto
        image={deleted ? null : user.avatar}
        name={deleted ? '' : initials(user.fullname)}
        classStylePhoto="commentAvatar"
      />
      <div className="commentMain">
        {deleted ? (
          <p className="commentDeleted">This comment was deleted.</p>
        ) : (
          <>
            <div className="commentMeta">
              <span className="commentAuthor">{user.fullname}</span>
              {photos.length && !body ? (
                <span className="commentDid">
                  shared {photos.length === 1 ? 'a photo' : `${photos.length} photos`}
                </span>
              ) : null}
              <span className="commentTime" title={moment(created_at).format('LLL')}>
                {moment(created_at).fromNow()}
              </span>
              {showGroup ? <span className="commentGroup">{group_name}</span> : null}
            </div>
            {reply_to_name && comment.parent_id !== comment.thread_id ? (
              <p className="commentReplyTo">Replying to {reply_to_name}</p>
            ) : null}
            {body ? <p className="commentBody">{body}</p> : null}
            <CommentPhotos photos={photos} author={user.fullname} onOpen={onOpenPhoto} />
            <div className="commentActions">
              <button type="button" className="commentLinkButton" onClick={() => onReply(comment)}>
                Reply
              </button>
              {can_delete ? (
                <button
                  type="button"
                  className="commentLinkButton commentDelete"
                  onClick={() => onDelete(comment)}
                >
                  Delete
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// The oldest entry in the activity: when the recipe was added
const RecipeAdded = ({ recipe }) => {
  const person = recipe?.addedBy?.fullname ? recipe.addedBy : recipe?.author
  if (!person?.fullname || !recipe?.created_at) return null

  return (
    <div className="commentThread activityAdded">
      <div className="comment">
        <UserPhoto
          image={person.avatar}
          name={initials(person.fullname)}
          classStylePhoto="commentAvatar"
        />
        <div className="commentMain">
          <div className="commentMeta">
            <span className="commentAuthor">{person.fullname}</span>
            <span className="commentDid">added this recipe</span>
            <span className="commentTime" title={moment(recipe.created_at).format('LLL')}>
              {moment(recipe.created_at).format('LL')}
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The recipe's activity: posts, photos and replies. Inside a group, that group's
 * conversation. On your own recipe outside a group, the conversations from every group
 * it is shared in.
 *
 * Every photo posted shows at the top of the activity and in its own post. onPhotos(photos)
 * hears about them (see photosFromThreads) so the page can show them full screen with
 * onOpenPhoto(photo).
 */
const RecipeComments = ({ recipeId, groupId, recipe, onPhotos, onOpenPhoto }) => {
  const location = useLocation()
  const [threads, setThreads] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [replyingTo, setReplyingTo] = useState(null)
  const highlightedId = Number((location.hash.match(/^#comment-(\d+)$/) || [])[1])
  const allGroups = !groupId
  const photos = useMemo(() => photosFromThreads(threads), [threads])

  const load = useCallback(
    () =>
      getComments(recipeId, groupId)
        .then(({ data }) => setThreads(data || []))
        .catch(() => setThreads([]))
        .finally(() => setLoaded(true)),
    [recipeId, groupId]
  )

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (onPhotos) onPhotos(photos)
  }, [photos])

  // Opened from an email's "Add a photo" link (#comments): bring the Activity box into
  // view, again once photos above it have loaded and moved it down
  useEffect(() => {
    if (loaded && location.hash === '#comments') {
      const timers = [100, 1000].map((ms) =>
        setTimeout(
          () => document.getElementById('comments')?.scrollIntoView({ block: 'start' }),
          ms
        )
      )
      return () => timers.forEach(clearTimeout)
    }
  }, [loaded, location.hash])

  // Opened from a notification: bring that comment into view
  useEffect(() => {
    if (loaded && highlightedId) {
      // After this render has reached the page
      const timer = setTimeout(() =>
        document
          .getElementById(`comment-${highlightedId}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      )
      return () => clearTimeout(timer)
    }
  }, [loaded, highlightedId])

  const post = (body, photos) =>
    createComment(recipeId, { groupId, body, photos }).then(() => {
      trackAction('posted_activity', {
        recipe_id: Number(recipeId),
        photo_count: photos?.length || 0,
      })
      return load()
    })

  const postReply = (body, photos) =>
    createComment(recipeId, {
      groupId: replyingTo.group_id,
      body,
      photos,
      parentId: replyingTo.id,
    }).then(() => {
      trackAction('posted_activity', {
        recipe_id: Number(recipeId),
        photo_count: photos?.length || 0,
        is_reply: true,
      })
      setReplyingTo(null)
      return load()
    })

  const remove = (comment) => {
    const what = comment.photos?.length ? 'post and its photos' : 'comment'
    if (window.confirm(`Delete this ${what}? This cannot be undone.`)) {
      deleteComment(comment.id)
        .then(() => load())
        .catch(() => window.alert('The comment could not be deleted. Please try again.'))
    }
  }

  const commentProps = {
    showGroup: allGroups,
    onReply: setReplyingTo,
    onDelete: remove,
    onOpenPhoto: (photo) => onOpenPhoto && onOpenPhoto(photo),
  }

  return (
    <section className="recipeComments" id="comments">
      <p className="recipeTitle">Activity</p>
      <FamilyPhotos photos={photos} onOpen={commentProps.onOpenPhoto} />

      {allGroups ? (
        <p className="commentsNote">
          Posts happen inside each group, so only that group sees them. Open this recipe from one of
          your groups to share a photo or start a conversation.
        </p>
      ) : (
        <CommentForm
          inputId="activityPhotos"
          placeholder="Made it? Share a photo, a tip, or a memory"
          submitText="Post"
          onSubmit={post}
        />
      )}

      {loaded && !threads.length ? (
        <p className="commentsEmpty">
          {allGroups
            ? 'No one has posted about this recipe yet.'
            : 'Nothing here yet. Made this recipe? Share a photo of how it turned out.'}
        </p>
      ) : null}

      <div className="commentThreads">
        {threads.map((thread) => {
          const threadIds = [thread.id, ...thread.replies.map(({ id }) => id)]
          const replyingHere = replyingTo && threadIds.includes(replyingTo.id)

          return (
            <div key={thread.id} className="commentThread">
              <Comment
                comment={thread}
                highlighted={highlightedId === thread.id}
                {...commentProps}
              />
              {thread.replies.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  highlighted={highlightedId === reply.id}
                  {...commentProps}
                />
              ))}
              {replyingHere ? (
                <div className="commentReplyForm">
                  <CommentForm
                    autoFocus
                    inputId={`replyPhotos-${thread.id}`}
                    placeholder={`Reply to ${replyingTo.user.fullname}`}
                    submitText="Reply"
                    onSubmit={postReply}
                    onCancel={() => setReplyingTo(null)}
                  />
                </div>
              ) : null}
            </div>
          )
        })}
        {loaded ? <RecipeAdded recipe={recipe} /> : null}
      </div>
    </section>
  )
}

export default RecipeComments
